import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Minus, Plus, ShoppingCart, MessageCircle, Truck, ShieldCheck,
  ChevronRight, ChevronLeft, Share2, FileText, Ruler, CreditCard, Info, Heart, Star,
} from "lucide-react";import { api, trackWaClick } from "@/lib/api";
import { fmtBRL, buildWhatsAppUrl, productPublicUrl } from "@/lib/format";
import { cashPrice, cardPrice, maxInstallments, installmentsOf } from "@/lib/pricing";
import { imgVariant, imgSrcSet, preloadImage } from "@/lib/img";
import { trackViewContent } from "@/lib/tracking";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { useCustomer } from "@/context/CustomerContext";
import { toast } from "sonner";
import { ProductCard } from "@/components/store/ProductCard";
import { DeliveryCountdown } from "@/components/store/DeliveryCountdown";
import { ProductReviews } from "@/components/store/ProductReviews";
import { ShippingCalculator } from "@/components/store/ShippingCalculator";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const settings = useSettings();
  const { favorites, toggleFavorite } = useCustomer();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [productReviews, setProductReviews] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [image, setImage] = useState(0);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const galleryDragX = useRef(null);
  const galleryDragged = useRef(false);
  const [lightbox, setLightbox] = useState(false);

  // teclado na foto ampliada: ESC fecha, setas navegam
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") galleryGoRef.current(1);
      if (e.key === "ArrowLeft") galleryGoRef.current(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);
  const galleryGoRef = useRef(() => {});
  galleryGoRef.current = (dir) => {
    if (!product) return;
    const total = galleryImages.length;
    if (total < 2) return;
    setImage((i) => (i + dir + total) % total);
  };

  useEffect(() => {
    setLoading(true);
    setImage(0);
    setQty(1);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        // primeira cor disponível como padrão
        const firstAvailable = (r.data.color_variants || []).find((v) => v.available !== false);
        setColor(firstAvailable?.name || r.data.colors?.[0] || null);
        trackViewContent(r.data);
        // pré-carrega a foto principal de cada cor: troca de cor instantânea
        (r.data.color_variants || []).forEach((v) => {
          const u = v.main_image || v.images?.[0];
          if (u) preloadImage(u, "full");
        });
      })
      .catch(() => setProduct(null))
      .finally(() => setLoading(false));
    api.get(`/products/${id}/related`).then((r) => setRelated(r.data)).catch(() => {});
    api.get(`/reviews`, { params: { product_id: id } }).then((r) => setProductReviews(r.data)).catch(() => {});
    api.get(`/delivery/forecast`, { params: { product_id: id } }).then((r) => setForecast(r.data)).catch(() => {});
  }, [id]);

  // variações de cor: galeria própria por cor (sem misturar fotos de cores diferentes)
  const variants = product?.color_variants || [];
  const colorOptions = variants.length
    ? variants
    : (product?.colors || []).map((name) => ({ name, images: [], main_image: "", available: true }));
  const selectedVariant = colorOptions.find((v) => v.name === color);
  const colorUnavailable = !!selectedVariant && selectedVariant.available === false;

  // fotos da cor selecionada (principal primeiro); cor sem fotos próprias = galeria geral
  const galleryImages = (() => {
    if (selectedVariant?.images?.length) {
      const rest = selectedVariant.images.filter((img) => img !== selectedVariant.main_image);
      return selectedVariant.main_image ? [selectedVariant.main_image, ...rest] : selectedVariant.images;
    }
    return product?.images || [];
  })();

  const selectColor = (c) => {
    setColor(c);
    setImage(0); // galeria da cor começa pela foto principal dela
  };

  if (loading) {
    return (
      <div className="container-store py-10">
        <div className="grid md:grid-cols-2 gap-10 animate-pulse">
          <div className="aspect-square bg-gray-100 rounded-2xl" />
          <div className="space-y-4">
            <div className="h-8 bg-gray-100 rounded w-3/4" />
            <div className="h-24 bg-gray-100 rounded" />
            <div className="h-24 bg-gray-100 rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container-store py-20 text-center" data-testid="product-not-found">
        <h1 className="font-heading text-2xl font-bold text-gray-900">Produto não encontrado</h1>
        <Link to="/produtos">
          <Button className="mt-6 rounded-full" data-testid="back-to-catalog">Voltar ao catálogo</Button>
        </Link>
      </div>
    );
  }

  const out = product.stock <= 0;
  const cash = cashPrice(product);
  const card = cardPrice(product);
  const nInst = maxInstallments(product);
  const installmentValue = installmentsOf(card, nInst)[0];
  // mensagem de interesse com o produto identificado (nome exato, cor e link público permanente)
  const waMessage = [
    "Olá! Tenho interesse neste produto:",
    product.name,
    color ? `Cor: ${color}` : null,
    `Quantidade: ${qty}`,
    `Valor: ${fmtBRL(cash * qty)} (ou até ${nInst}x de ${fmtBRL(installmentsOf(card * qty, nInst)[0])} no cartão)`,
    productPublicUrl(product.id, settings.company_domain),
  ]
    .filter(Boolean)
    .join("\n");

  // navegação da galeria grande: setas + arrastar/deslizar
  const galleryGo = (dir) => {
    if (galleryImages.length < 2) return;
    setImage((i) => (i + dir + galleryImages.length) % galleryImages.length);
  };
  const onGalleryPointerDown = (e) => {
    galleryDragX.current = e.clientX;
    galleryDragged.current = false;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* sem captura em alguns navegadores */
    }
  };
  const onGalleryPointerMove = (e) => {
    if (galleryDragX.current == null) return;
    if (Math.abs(e.clientX - galleryDragX.current) > 8) galleryDragged.current = true;
  };
  const onGalleryPointerUp = (e) => {
    if (galleryDragX.current == null) return;
    const dx = e.clientX - galleryDragX.current;
    galleryDragX.current = null;
    if (Math.abs(dx) > 40) galleryGo(dx < 0 ? 1 : -1);
  };
  const onGalleryClick = () => {
    if (galleryDragged.current) {
      galleryDragged.current = false;
      return; // foi arrasto, não clique
    }
    setLightbox(true); // ampliar a foto
  };

  const buyOnSite = () => {
    addItem(product, color, qty);
    navigate("/carrinho");
  };

  const dimensions = [
    product.height && `Altura: ${product.height}`,
    product.width && `Largura: ${product.width}`,
    product.depth && `Profundidade: ${product.depth}`,
  ].filter(Boolean);

  // avisos importantes: avisos globais do painel + observações específicas do produto
  const importantNotices = [
    ...(settings.important_info_text || "").split("\n"),
    ...(product.important_notes || "").split("\n"),
  ].map((s) => s.trim()).filter(Boolean);

  return (
    <div className="container-store py-6 sm:py-10" data-testid="product-page">
      <Seo
        title={product.seo_title || `${product.name} | HG Móveis`}
        description={product.seo_description || `${product.name}: ${fmtBRL(cash)} ou em até ${nInst}x de ${fmtBRL(installmentValue)} no cartão de crédito. Pagamento na entrega e garantia.`}
        canonical={`/produto/${product.id}`}
        ogImage={galleryImages?.[0] || undefined}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": product.name,
          "description": (product.seo_description || product.description || product.name).slice(0, 500),
          "image": (product.images || []).filter(Boolean).slice(0, 5).map((i) => (i.startsWith("http") ? i : `https://comprenahg.com.br${i}`)),
          "sku": product.sku || product.id,
          "brand": { "@type": "Brand", "name": product.manufacturer || "HG Móveis" },
          "offers": {
            "@type": "Offer",
            "url": `https://comprenahg.com.br/produto/${product.id}`,
            "priceCurrency": "BRL",
            "price": Number(cash).toFixed(2),
            "availability": product.stock > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
          },
          // estrelas do Google: somente com avaliações REais aprovadas, visíveis nesta página
          ...(productReviews.length > 0 && {
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": (productReviews.reduce((s, r) => s + (r.rating || 5), 0) / productReviews.length).toFixed(1),
              "reviewCount": productReviews.length,
            },
            "review": productReviews.slice(0, 10).map((r) => ({
              "@type": "Review",
              "author": { "@type": "Person", "name": r.name },
              "reviewRating": { "@type": "Rating", "ratingValue": r.rating || 5 },
              "reviewBody": (r.text || "").slice(0, 500),
            })),
          }),
        }}
      />

      {/* breadcrumb */}
      <nav className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 mb-6" data-testid="breadcrumb">
        <Link to="/" className="hover:text-brand">Início</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <Link to="/produtos" className="hover:text-brand">Produtos</Link>
        {product.category && (
          <>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to={`/categoria/${product.category.slug}`} className="hover:text-brand">
              {product.category.name}
            </Link>
          </>
        )}
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-gray-800 font-medium truncate max-w-[160px] sm:max-w-none">{product.name}</span>
      </nav>

      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        {/* galeria */}
        <div data-testid="product-gallery">
          <div
            className="relative bg-white rounded-2xl overflow-hidden aspect-square border border-gray-100 group/pdpgallery select-none cursor-zoom-in"
            onPointerDown={onGalleryPointerDown}
            onPointerMove={onGalleryPointerMove}
            onPointerUp={onGalleryPointerUp}
            onPointerCancel={() => (galleryDragX.current = null)}
            onClick={onGalleryClick}
            style={{ touchAction: "pan-y" }}
            title="Clique para ampliar"
          >
            <img
              src={imgVariant(galleryImages[image], "full") || "/assets/logo.png"}
              srcSet={imgSrcSet(galleryImages[image])}
              sizes="(max-width: 768px) 100vw, 50vw"
              alt={product.name}
              className="w-full h-full object-contain p-2 transition-opacity duration-300"
              draggable={false}
              decoding="async"
              fetchPriority="high"
              data-testid="product-main-image"
            />
            {product.is_demo && (
              <span className="absolute top-3 right-3 bg-slate-800/80 text-white text-xs font-medium rounded-full px-2.5 py-1">
                Demonstração
              </span>
            )}
            {galleryImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={() => galleryGo(-1)}
                  onPointerDown={(e) => e.stopPropagation()}
                  aria-label="Foto anterior"
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center text-gray-700 opacity-70 sm:opacity-0 sm:group-hover/pdpgallery:opacity-100 transition-opacity"
                  data-testid="pdp-gallery-prev"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  type="button"
                  onClick={() => galleryGo(1)}
                  onPointerDown={(e) => e.stopPropagation()}
                  aria-label="Próxima foto"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/85 hover:bg-white shadow flex items-center justify-center text-gray-700 opacity-70 sm:opacity-0 sm:group-hover/pdpgallery:opacity-100 transition-opacity"
                  data-testid="pdp-gallery-next"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
                <span
                  className="absolute bottom-2.5 right-2.5 z-10 bg-black/45 text-white text-[11px] font-medium rounded-full px-2.5 py-0.5"
                  data-testid="pdp-gallery-counter"
                >
                  {image + 1}/{galleryImages.length}
                </span>
              </>
            )}
          </div>
          {galleryImages.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">
              {galleryImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setImage(i)}
                  className={`shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                    i === image ? "border-brand" : "border-transparent opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`gallery-thumb-${i}`}
                >
                  <img src={imgVariant(img, "thumb")} alt="" loading="lazy" decoding="async" className="w-full h-full object-contain bg-white" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* infos */}
        <div>
          {product.category && (
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand">
              {product.category.name}
            </span>
          )}
          <h1 className="mt-2 font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" data-testid="product-name">
            {product.name}
          </h1>
          {(product.manufacturer || product.model || product.sku) && (
            <p className="mt-1 text-xs text-gray-500" data-testid="product-meta">
              {[product.manufacturer && `Fabricante: ${product.manufacturer}`,
                product.model && `Modelo: ${product.model}`,
                product.sku && `Cód: ${product.sku}`].filter(Boolean).join(" • ")}
            </p>
          )}

          {/* preço: valor em destaque + parcelamento discreto abaixo, sem rótulos */}
          <div className="mt-4" data-testid="product-price-box">
            <p className="font-heading text-3xl sm:text-4xl font-bold text-[#D71920]" data-testid="price-cash">
              {fmtBRL(cash)}
            </p>
            <p
              className="mt-1.5 flex items-center gap-2 text-sm sm:text-base text-gray-600"
              data-testid="installments-label"
            >
              <CreditCard className="w-4 h-4 text-brand shrink-0" />
              <span>
                Ou até <strong className="text-gray-900">{nInst}x de {fmtBRL(installmentValue)}</strong> no cartão de crédito
              </span>
            </p>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <span
              className="inline-flex items-center gap-1.5 bg-gray-50 border border-gray-100 text-gray-600 rounded-full px-3 py-1 text-[11px] font-medium"
              data-testid="badge-delivery-payment"
            >
              <Truck className="w-3.5 h-3.5 text-brand" /> Pagamento na entrega
            </span>
          </div>

          {/* previsão de entrega real (calendário + capacidade + estoque) — sem urgência falsa */}
          {forecast?.available && !out && (
            <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl px-4 py-3 flex items-center gap-3" data-testid="delivery-forecast">
              <Truck className="w-5 h-5 text-brand shrink-0" />
              <div className="text-sm text-gray-700">
                {forecast.is_tomorrow && forecast.show_countdown ? (
                  <>
                    <p>
                      <strong className="text-brand">{forecast.text_tomorrow || "Entrega amanhã"}</strong>{" "}
                      <span className="text-gray-500">({forecast.next_date_label})</span>
                    </p>
                    {forecast.show_countdown && forecast.seconds_to_cutoff > 0 && (
                      <p className="mt-1">
                        <DeliveryCountdown
                          secondsToCutoff={forecast.seconds_to_cutoff}
                          label={forecast.countdown_label}
                          suffix={forecast.countdown_suffix || ""}
                        />
                      </p>
                    )}
                    {forecast.countdown_suffix ? null : (
                      <p className="text-gray-500 text-xs mt-0.5">Nas regiões atendidas — confirme a data no checkout.</p>
                    )}
                  </>
                ) : (
                  <p>
                    Entrega a partir de <strong className="text-brand">{forecast.next_date_label}</strong>
                    <span className="text-gray-500"> nas regiões atendidas — escolha a data no checkout.</span>
                  </p>
                )}
              </div>
            </div>
          )}

          {colorOptions.length > 0 && (
            <div className="mt-6" data-testid="color-selector">
              <p className="text-sm font-semibold text-gray-800">
                Escolha a cor: <span className="text-brand" data-testid="selected-color">{color}</span>
                {colorUnavailable && (
                  <span className="ml-2 text-xs font-bold text-red-500" data-testid="color-unavailable-note">
                    (esgotada no momento)
                  </span>
                )}
              </p>
              <div className="mt-3 flex flex-wrap gap-3">
                {colorOptions.map((v) => {
                  const thumb = v.main_image || v.images?.[0] || product.images?.[0];
                  const unavailable = v.available === false;
                  const selected = color === v.name;
                  return (
                    <button
                      key={v.name}
                      type="button"
                      onClick={() => selectColor(v.name)}
                      title={v.name + (unavailable ? " (esgotada)" : "")}
                      aria-pressed={selected}
                      className="flex flex-col items-center gap-1.5"
                      data-testid={`color-option-${v.name.toLowerCase()}`}
                    >
                      <span
                        className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border-2 bg-white transition-all ${
                          selected
                            ? "border-brand ring-2 ring-brand/25 shadow-md"
                            : "border-gray-200 hover:border-brand/50 hover:shadow"
                        } ${unavailable ? "opacity-45" : ""}`}
                      >
                        {thumb ? (
                          <img src={imgVariant(thumb, "thumb")} alt={v.name} loading="lazy" decoding="async" className="w-full h-full object-contain p-1" draggable={false} />
                        ) : (
                          <span className="block w-full h-full bg-gradient-to-br from-slate-100 to-slate-300" />
                        )}
                        {unavailable && (
                          <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                            <span className="block w-[135%] h-0.5 bg-red-500/80 -rotate-[30deg] rounded" />
                          </span>
                        )}
                      </span>
                      <span
                        className={`text-[11px] leading-tight text-center max-w-[5.5rem] ${
                          selected ? "text-brand font-bold" : "text-gray-600 font-medium"
                        }`}
                      >
                        {v.name}
                        {unavailable && (
                          <span className="block text-[9px] font-bold uppercase text-red-500">esgotada</span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>
              {colorUnavailable && (
                <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2" data-testid="color-unavailable-warning">
                  Esta cor está esgotada no momento. Fale com a equipe pelo WhatsApp para consultar a reposição.
                </p>
              )}
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border border-gray-200 rounded-full">
              <button
                className="p-2.5 text-gray-600 hover:text-brand disabled:opacity-40"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                disabled={qty <= 1}
                data-testid="qty-decrease"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-10 text-center font-semibold" data-testid="qty-value">{qty}</span>
              <button
                className="p-2.5 text-gray-600 hover:text-brand disabled:opacity-40"
                onClick={() => setQty((q) => Math.min(product.stock || 99, q + 1))}
                disabled={qty >= (product.stock || 99)}
                data-testid="qty-increase"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500" data-testid="stock-info">
              {out ? "Produto indisponível" : colorUnavailable ? "Cor esgotada — consulte reposição" : product.stock <= 5 ? `Últimas ${product.stock} unidades` : "Disponível para entrega"}
            </p>
          </div>

          {/* dois botões de compra + favorito */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              size="lg"
              className="rounded-full font-semibold active:scale-95 transition-transform"
              disabled={out || colorUnavailable}
              onClick={buyOnSite}
              data-testid="product-buy-site"
            >
              <ShoppingCart className="w-5 h-5" /> Comprar pelo Site
            </Button>
            {out || colorUnavailable ? (
              <Button
                size="lg"
                variant="outline"
                className="w-full rounded-full font-semibold border-green-600 text-green-700"
                disabled
                data-testid="product-buy-whatsapp"
              >
                <MessageCircle className="w-5 h-5" /> Comprar pelo WhatsApp
              </Button>
            ) : (
              <a
                href={buildWhatsAppUrl(waMessage, settings.whatsapp_number)}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackWaClick(product.id, "produto")}
              >
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full rounded-full font-semibold border-green-600 text-green-700 hover:bg-green-50 active:scale-95 transition-transform"
                  data-testid="product-buy-whatsapp"
                >
                  <MessageCircle className="w-5 h-5" /> Comprar pelo WhatsApp
                </Button>
              </a>
            )}
          </div>

          <button
            type="button"
            onClick={async () => {
              const fav = await toggleFavorite(product.id);
              toast.success(fav ? "Adicionado aos favoritos" : "Removido dos favoritos");
            }}
            className="mt-3 w-full flex items-center justify-center gap-2 text-sm font-semibold rounded-full border border-gray-200 py-2.5 text-gray-600 hover:border-red-300 hover:text-red-500 transition-colors active:scale-95"
            data-testid="product-favorite"
          >
            <Heart className={`w-4 h-4 ${favorites.includes(product.id) ? "fill-red-500 text-red-500" : ""}`} />
            {favorites.includes(product.id) ? "Remover dos favoritos" : "Salvar nos favoritos"}
          </button>

          <button
            onClick={() => {
              const shareUrl = `https://wa.me/?text=${encodeURIComponent(
                `Olha esse móvel que encontrei na HG Móveis: ${product.name} — ${fmtBRL(cash)} ${productPublicUrl(product.id, settings.company_domain)}`
              )}`;
              window.open(shareUrl, "_blank");
            }}
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-brand font-medium transition-colors"
            data-testid="product-share-whatsapp"
          >
            <Share2 className="w-4 h-4" /> Compartilhar este produto no WhatsApp
          </button>

          {/* calculadora de frete */}
          <div className="mt-6">
            <ShippingCalculator />
          </div>
        </div>
      </div>

      {/* informações completas do produto — sempre visíveis, sem acordeão/abas */}
      <section className="mt-12 sm:mt-16 max-w-4xl space-y-10" data-testid="product-info-sections">
        {product.description && (
          <div data-testid="info-description">
            <h2 className="flex items-center gap-2.5 font-heading text-lg sm:text-xl font-bold text-gray-900 tracking-tight uppercase">
              <FileText className="w-5 h-5 text-brand shrink-0" /> Descrição do produto
            </h2>
            <div className="mt-2 h-1 w-16 rounded-full bg-brand" />
            <p className="mt-4 text-sm sm:text-base text-gray-600 leading-relaxed whitespace-pre-line">
              {product.description}
            </p>
          </div>
        )}

        {(dimensions.length > 0 || product.materials || product.sizes?.length > 0) && (
          <div className="pt-8 border-t border-gray-100" data-testid="info-specs">
            <h2 className="flex items-center gap-2.5 font-heading text-lg sm:text-xl font-bold text-gray-900 tracking-tight uppercase">
              <Ruler className="w-5 h-5 text-brand shrink-0" /> Medidas e especificações técnicas
            </h2>
            <div className="mt-2 h-1 w-16 rounded-full bg-brand" />
            <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
              {product.height && (
                <div className="flex justify-between sm:justify-start sm:gap-3 border-b border-dashed border-gray-200 pb-2">
                  <dt className="text-sm font-semibold text-gray-800">Altura</dt>
                  <dd className="text-sm text-gray-600">{product.height}</dd>
                </div>
              )}
              {product.width && (
                <div className="flex justify-between sm:justify-start sm:gap-3 border-b border-dashed border-gray-200 pb-2">
                  <dt className="text-sm font-semibold text-gray-800">Largura</dt>
                  <dd className="text-sm text-gray-600">{product.width}</dd>
                </div>
              )}
              {product.depth && (
                <div className="flex justify-between sm:justify-start sm:gap-3 border-b border-dashed border-gray-200 pb-2">
                  <dt className="text-sm font-semibold text-gray-800">Profundidade</dt>
                  <dd className="text-sm text-gray-600">{product.depth}</dd>
                </div>
              )}
              {product.materials && (
                <div className="flex justify-between sm:justify-start sm:gap-3 border-b border-dashed border-gray-200 pb-2">
                  <dt className="text-sm font-semibold text-gray-800">Material</dt>
                  <dd className="text-sm text-gray-600">{product.materials}</dd>
                </div>
              )}
            </dl>
            {(product.sizes || []).length > 0 && (
              <ul className="mt-3 space-y-1.5" data-testid="info-specs-extra">
                {product.sizes.map((s) => (
                  <li key={s} className="flex items-start gap-2 text-sm text-gray-600">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-brand shrink-0" /> {s}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="pt-8 border-t border-gray-100" data-testid="info-warranty">
          <h2 className="flex items-center gap-2.5 font-heading text-lg sm:text-xl font-bold text-gray-900 tracking-tight uppercase">
            <ShieldCheck className="w-5 h-5 text-brand shrink-0" /> Garantia
          </h2>
          <div className="mt-2 h-1 w-16 rounded-full bg-brand" />
          <p className="mt-4 text-sm sm:text-base text-gray-600 leading-relaxed whitespace-pre-line">
            {product.warranty_text || settings.warranty_text}
          </p>
        </div>

        <div className="pt-8 border-t border-gray-100" data-testid="info-important">
          <h2 className="flex items-center gap-2.5 font-heading text-lg sm:text-xl font-bold text-gray-900 tracking-tight uppercase">
            <Info className="w-5 h-5 text-brand shrink-0" /> Informações importantes
          </h2>
          <div className="mt-2 h-1 w-16 rounded-full bg-brand" />
          {importantNotices.length > 0 ? (
            <ul className="mt-4 space-y-2.5">
              {importantNotices.map((n, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm sm:text-base text-gray-500 leading-relaxed">
                  <span className="mt-2 w-1 h-1 rounded-full bg-gray-400 shrink-0" />
                  <span>{n}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-gray-400">Nenhum aviso cadastrado no momento.</p>
          )}
        </div>
      </section>

      {/* avaliações reais do produto: média, comentários aprovados e formulário com moderação */}
      <ProductReviews productId={id} reviews={productReviews} />

      {related.length > 0 && (
        <section className="mt-16" data-testid="related-products">
          <h2 className="font-heading text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
            Você também pode gostar
          </h2>
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}

      {/* foto ampliada (tela cheia) */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
          data-testid="pdp-lightbox"
          role="dialog"
          aria-label="Foto ampliada do produto"
        >
          <button
            type="button"
            onClick={() => setLightbox(false)}
            aria-label="Fechar foto ampliada"
            className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xl"
            data-testid="pdp-lightbox-close"
          >
            ×
          </button>
          <img
            src={imgVariant(galleryImages[image], "full")}
            alt={`${product.name} ampliado`}
            className="max-w-[92vw] max-h-[88vh] object-contain select-none"
            draggable={false}
            onClick={(e) => e.stopPropagation()}
            data-testid="pdp-lightbox-image"
          />
          {galleryImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); galleryGo(-1); }}
                aria-label="Foto anterior"
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="pdp-lightbox-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); galleryGo(1); }}
                aria-label="Próxima foto"
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="pdp-lightbox-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
              <span className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-sm">
                {image + 1}/{galleryImages.length}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
